import { useQueryClient } from '@tanstack/react-query'

import {
  getListConversationItemsApiV1ConversationItemsGetQueryKey,
  getListConversationsApiV1ConversationsGetQueryKey,
  useSendSmsApiV1SendSmsPost,
  useUploadFilesApiV1FilesPost,
} from '#/shared/api/generated/communication/theCargoCommunication'

import { sanitizeSmsText } from '#/shared/lib/sms-sanitize'

import { broadcastSmsSent } from '../lib/cross-tab-sync'
import type { ComposerAttachment } from './use-composer'

interface SendMessageArgs {
  /** Sending line id (the active line from `useActiveLine`). */
  userPhoneId: string | null
  /** Destination number in any format; the backend normalizes it. */
  toNumber: string
  text: string
  attachments: ReadonlyArray<ComposerAttachment>
}

interface SendResult {
  /** Whether the SMS was queued successfully. */
  ok: boolean
  /**
   * The thread the message landed in, taken from the send response. Lets the
   * caller re-point an open chat at the real conversation (e.g. when it was
   * opened by number and had no id yet). `null` when the send failed or the
   * response didn't carry one.
   */
  conversationId: string | null
  /**
   * Machine-readable rejection reason when the backend declined the send
   * inside a 200 response (`success: false`) — e.g. OPTED_OUT, ROUTE_BLOCKED,
   * CONTACT_UNDELIVERABLE, IDEMPOTENT. `null` on success and on transport
   * errors. Feed it to `sendErrorMessage` for the user-facing toast.
   */
  errorCode: string | null
  /**
   * The backend's verbatim human-readable rejection reason, e.g. "Recipient
   * opted out of text messages — they replied STOP to your line … on Jul 23".
   * Richer than the per-code static line, so `sendErrorMessage` prefers it.
   * `null` on success and on transport errors.
   */
  errorMessage: string | null
}

interface SendMessage {
  send: (args: SendMessageArgs) => Promise<SendResult>
  isSending: boolean
}

/**
 * The thread the message landed in. The generated `SendSMSQueueResponse` model
 * doesn't declare `conversation_id` yet (the OpenAPI schema is behind the live
 * endpoint), so read it via a narrow cast until the schema catches up and orval
 * regenerates the field.
 */
function readConversationId(data: unknown): string | null {
  return (data as { conversation_id?: string | null }).conversation_id ?? null
}

type UploadMutation = ReturnType<typeof useUploadFilesApiV1FilesPost>

/**
 * Upload all staged files in one `POST /files` request; the carrier fetches
 * MMS media from each returned public `url`.
 */
async function buildMediaUrls(
  upload: UploadMutation,
  attachments: ReadonlyArray<ComposerAttachment>,
): Promise<string[]> {
  if (attachments.length === 0) return []
  // The generated body types `files` as string[] (binary), but the
  // client appends each File to FormData — File is the runtime value.
  const uploaded = await upload.mutateAsync({
    data: { files: attachments.map((a) => a.file) as unknown as string[] },
  })
  return uploaded.data.map((file) => file.url)
}

/**
 * Composer send pipeline: upload any staged attachments to get public URLs,
 * then queue the SMS/MMS. Returns success/failure so the caller can settle or
 * undo its optimistic bubble. On success it refreshes the thread list + unread
 * counts and refetches the open thread's items so the sent message is written
 * into the timeline cache — otherwise it lives only in the ephemeral outbox
 * overlay and vanishes once the thread is closed (until a full refresh). The
 * overlay isn't swapped out mid-session: the timeline merge dedupes the server
 * echo against the optimistic bubble (see `overlayDupIds`).
 */
export function useSendMessage(): SendMessage {
  const queryClient = useQueryClient()
  const upload = useUploadFilesApiV1FilesPost()
  const sendSms = useSendSmsApiV1SendSmsPost()

  const send = async ({
    userPhoneId,
    toNumber,
    text,
    attachments,
  }: SendMessageArgs): Promise<SendResult> => {
    if (!userPhoneId || !toNumber) {
      return {
        ok: false,
        conversationId: null,
        errorCode: null,
        errorMessage: null,
      }
    }
    try {
      const media = await buildMediaUrls(upload, attachments)

      // Sanitize on the way out no matter which surface built `text` (composer,
      // compose-new) — fancy Unicode font gets spam-blocked by Telnyx.
      const sent = await sendSms.mutateAsync({
        data: {
          user_phone_id: userPhoneId,
          to_number: toNumber,
          message: sanitizeSmsText(text) || undefined,
          media_urls: media.length > 0 ? media : undefined,
        },
      })
      // The queue endpoint rejects some sends inside a 200 (`success: false`,
      // e.g. the contact replied STOP after this thread was opened). Nothing
      // was queued or saved, so no `sms_status` event will ever flip the
      // bubble — treat it as a failure here or the optimistic ✓ sticks around
      // until a refresh silently drops the message.
      if (!sent.data.success) {
        return {
          ok: false,
          conversationId: null,
          errorCode: sent.data.error_code ?? null,
          errorMessage: sent.data.error ?? null,
        }
      }
      const conversationId = readConversationId(sent.data)

      void queryClient.invalidateQueries({
        queryKey: getListConversationsApiV1ConversationsGetQueryKey(),
      })
      void queryClient.invalidateQueries({
        queryKey: getListConversationItemsApiV1ConversationItemsGetQueryKey(),
      })
      // Sibling tabs showing this thread refetch it right away (see
      // cross-tab-sync); their outbox holds nothing for this send.
      broadcastSmsSent({ phone: toNumber })
      return { ok: true, conversationId, errorCode: null, errorMessage: null }
    } catch {
      return {
        ok: false,
        conversationId: null,
        errorCode: null,
        errorMessage: null,
      }
    }
  }

  return { send, isSending: upload.isPending || sendSms.isPending }
}
